import { compileAmbient, hasCompileAmbient } from '../janus-engine/compile/compile-ambient';
import { floorBandForPosition, floorBandRoomForPosition, type FloorBandBox } from './floor-band.js';
import { inventoryFixtureFlags } from '../janus-engine/inventory/solid-obstacle.js';

type Range = { min: number; max: number };

type InventoryRecord = Record<string, unknown>;

export interface CampaignInventoryPlacementReaders {
  item(source: InventoryRecord, key: string): unknown;
  layer(source: InventoryRecord, key: string): unknown;
}

export interface CampaignInventoryPlacementSemantics {
  itemId: string | null;
  itemType: string;
  forestPart: 'tree' | 'bush' | 'hedge' | 'herb' | null;
  immovable: boolean;
  integratedObject: boolean;
  /**
   * A gatherable resource node — the same signal Janus' gathering_roll()/excavation_roll() consume
   * (`gathering_time_int`), with the resource item_types as the fallback for template-thinned rows.
   * Catalog herbs are authored immovable=1, so this is what keeps them visible where scenery is not.
   */
  harvestable: boolean;
}

const HARVESTABLE_ITEM_TYPES = new Set(['herbal_plant', 'digsite']);

function inventoryScalar(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

function inventoryField(row: InventoryRecord, key: string): unknown {
  for (const candidate of [row, row.inventory, row.inventories, row.items, row.item_layer_types]) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)
      && Object.prototype.hasOwnProperty.call(candidate, key)) {
      return inventoryScalar((candidate as InventoryRecord)[key]);
    }
  }
  return null;
}

/**
 * Recover play-mode interaction facts from a possibly template-thinned encounter inventory row.
 * The canonical item/layer readers receive the encounter root catalog because fixed-world facts
 * such as a tree's item_type and immovable layer may exist only on that catalog definition.
 */
export function campaignInventoryPlacementSemantics(
  row: InventoryRecord,
  catalog: unknown,
  readers: CampaignInventoryPlacementReaders,
): CampaignInventoryPlacementSemantics {
  const source = catalog && typeof catalog === 'object' && !Array.isArray(catalog)
    ? { ...row, catalog }
    : row;
  const itemType = String(
    readers.item(source, 'item_type') ?? inventoryField(row, 'item_type') ?? 'item',
  ).trim() || 'item';
  const normalizedItemType = itemType.toLowerCase().replace(/[\s-]+/g, '_');
  const forestPart = ['tree', 'bush', 'hedge', 'herb'].includes(normalizedItemType)
    ? normalizedItemType as CampaignInventoryPlacementSemantics['forestPart']
    : null;
  const itemId = String(
    inventoryField(row, 'inventory_item_id')
      ?? readers.item(source, 'item_id')
      ?? inventoryField(row, 'item_id')
      ?? '',
  ).trim();
  const definition = catalog && typeof catalog === 'object' && !Array.isArray(catalog)
    ? (catalog as InventoryRecord)[itemId] as InventoryRecord | undefined : undefined;

  const gatheringTime = inventoryScalar(
    readers.layer(source, 'gathering_time_int') ?? inventoryField(row, 'gathering_time_int'),
  );
  // A positive, finite gathering time (the layer gathering_roll()/excavation_roll() consume) — never a boolean,
  // blank, zero or malformed value. The resource item_types qualify by category alone.
  const gatheringSeconds = typeof gatheringTime === 'number' || (typeof gatheringTime === 'string' && gatheringTime.trim() !== '')
    ? Number(gatheringTime)
    : Number.NaN;
  const harvestable = HARVESTABLE_ITEM_TYPES.has(normalizedItemType)
    || (Number.isFinite(gatheringSeconds) && gatheringSeconds > 0);

  return {
    itemId: itemId || null,
    itemType,
    forestPart,
    ...inventoryFixtureFlags(key => readers.layer(source, key) ?? inventoryField(row, key)
      ?? (definition ? readers.layer(definition, key) : undefined)),
    harvestable,
  };
}

export interface CampaignDirectInteractionPlacement {
  itemId?: string | null;
  itemType?: string | null;
  forestPart?: string | null;
  immovable?: boolean;
  integratedObject?: boolean;
  harvestable?: boolean;
  reviewMetadata?: {
    controllable?: boolean;
    sourceEntityId?: string | null;
    legendaryCreature?: boolean;
    legendaryMovement?: boolean;
    statusEffects?: readonly { key: string }[];
  } | null;
}

export interface CampaignCreatureTurnMovementState {
  inCombat: boolean;
  turnPhase: string | null | undefined;
  currentTurnEntityId: string | null | undefined;
  forceMovement?: boolean;
}

export const CAMPAIGN_LEGENDARY_MOVEMENT_REQUIRED_MESSAGE =
  'This legendary creature cannot move outside its Main Turn without Legendary Movement.';
export const CAMPAIGN_NOT_YOUR_TURN_MOVEMENT_MESSAGE =
  'You cannot move when it is not your turn.';

/**
 * Frontend admission mirror of the authoritative PHP turn gates. In combat, ordinary gameplay
 * movement belongs to the creature named by the current turn pointer. A legendary creature also
 * needs Legendary Movement when that pointer names its Legendary Turn. A rejected attempt never
 * starts a body/altitude gesture or paints a destination preview; Ctrl/Force Movement remains the
 * explicit GM administrative override and Scene Edit remains outside gameplay movement.
 */
export function campaignCreatureTurnMovementGate(
  placement: CampaignDirectInteractionPlacement | null | undefined,
  kind: CampaignDirectInteractionKind,
  campaignEditMode: boolean,
  turn: CampaignCreatureTurnMovementState,
): CampaignInventoryWeightMoveGate {
  if (campaignEditMode || turn.forceMovement === true || kind !== 'creature') {
    return { accepted: true, message: '' };
  }
  // Status admission applies outside combat too, before a drag preview or playback starts.
  if (placement?.reviewMetadata?.statusEffects?.some(status => status.key === 'stunned')) {
    return { accepted: false, message: 'You cannot move while stunned.' };
  }
  if (turn.inCombat !== true) return { accepted: true, message: '' };
  const placementEntityId = String(placement?.reviewMetadata?.sourceEntityId ?? '').trim().toUpperCase();
  const currentTurnEntityId = String(turn.currentTurnEntityId ?? '').trim().toUpperCase();
  const ownsCurrentTurn = placementEntityId !== '' && placementEntityId === currentTurnEntityId;
  const ownsMainTurn = String(turn.turnPhase ?? '').trim().toLowerCase() === 'main'
    && ownsCurrentTurn;
  if (placement?.reviewMetadata?.legendaryCreature === true
    && placement?.reviewMetadata?.legendaryMovement !== true
    && !ownsMainTurn) {
    return { accepted: false, message: CAMPAIGN_LEGENDARY_MOVEMENT_REQUIRED_MESSAGE };
  }
  return ownsCurrentTurn
    ? { accepted: true, message: '' }
    : { accepted: false, message: CAMPAIGN_NOT_YOUR_TURN_MOVEMENT_MESSAGE };
}

/**
 * Item-backed trees participate in encounter inventory consequences (AOE, damage,
 * harvesting, replacement), but ordinary play-mode clicks treat them as passive scenery.
 */
export function isCampaignPassiveTreeInventory(
  placement: CampaignDirectInteractionPlacement | null | undefined,
): boolean {
  if (!placement) return false;
  const itemType = String(placement?.itemType ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return (placement.forestPart === 'tree' || itemType === 'tree')
    && typeof placement.itemId === 'string'
    && placement.itemId.trim() !== '';
}

/**
 * Scene-fixed inventory stays addressable for encounter consequences and explicit
 * information gestures without capturing ordinary campaign play input.
 */
export function isCampaignPassiveInformationPlacement(
  placement: CampaignDirectInteractionPlacement | null | undefined,
): boolean {
  return placement?.immovable === true
    || placement?.integratedObject === true
    || isCampaignPassiveTreeInventory(placement);
}

/**
 * The Alt / toggle inventory reveal labels what a player can DO something with, not the scenery
 * (owner 2026-09-16: "not trees and walls, but harvestable plants"). Movable inventory is always
 * eligible. Scene-fixed inventory is hidden unless it is a gatherable resource node or a container
 * (owner carve-out, same day: the campsite's immovable "Party Chest" is lootable and must read).
 * Trees never qualify. Wall runs and the kind gate are the caller's.
 */
export function campaignInventoryLabelEligible(
  placement: CampaignDirectInteractionPlacement | null | undefined,
): boolean {
  if (!placement) return false;
  if (!isCampaignPassiveInformationPlacement(placement)) return true;
  if (isCampaignPassiveTreeInventory(placement)) return false;
  const itemType = String(placement.itemType ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return placement.harvestable === true || itemType === 'container';
}

export type CampaignDirectInteractionKind = 'creature' | 'inventory' | 'prop' | 'status-effect';

/** Scene editing opts props and scene-fixed inventory back into direct selection and movement. */
export function campaignPlacementAllowsDirectInteraction(
  placement: CampaignDirectInteractionPlacement | null | undefined,
  kind: CampaignDirectInteractionKind,
  campaignEditMode: boolean,
): boolean {
  if (campaignEditMode) return true;
  if (kind === 'prop') return false;
  return kind !== 'inventory' || !isCampaignPassiveInformationPlacement(placement);
}

/** Targeting/inspection remains available for perceived creatures; movement is a separate grant. */
/** Permission-less warm snapshots do not revoke an already-authorized control capability. */
export function campaignActorControlAllowed(permissionKnown: boolean, playerPermission: unknown, previous = false): boolean {
  return permissionKnown ? playerPermission === true || playerPermission === 1 || playerPermission === '1' : previous;
}

export function campaignPlacementAllowsMovement(
  placement: CampaignDirectInteractionPlacement | null | undefined,
  kind: CampaignDirectInteractionKind,
  campaignEditMode: boolean,
): boolean {
  if (!campaignPlacementAllowsDirectInteraction(placement, kind, campaignEditMode)) return false;
  if (campaignEditMode) return true;
  if (kind === 'creature') return placement?.reviewMetadata?.controllable === true;
  return kind === 'inventory';
}

/**
 * With no acting creature selected, a campaign manager's inventory click is an administrative
 * transform edit rather than a physical pickup attempt. Keep the exception exact: it applies
 * only to stored encounter inventory in campaign play, never to players, creatures, or scenery
 * props, and selecting a caster immediately restores the ordinary weight-gated interaction.
 */
export function campaignGmInventoryTransformEditAllowed(
  canManageCampaign: boolean,
  hasActiveCaster: boolean,
  kind: CampaignDirectInteractionKind,
  storedEncounterInventory: boolean,
): boolean {
  return canManageCampaign
    && !hasActiveCaster
    && kind === 'inventory'
    && storedEncounterInventory;
}

/** @deprecated Retained for the staged rotation-only bundle while clients roll forward. */
export const campaignGmInventoryRotationEditAllowed = campaignGmInventoryTransformEditAllowed;

/** Shared yaw quantizer: ordinary editing uses 15°, while Shift requests quarter turns. */
export function campaignPlacementRotationSnapDegrees(
  startRotationDegrees: number,
  pointerDeltaDegrees: number,
  shiftKey: boolean,
): number {
  const increment = shiftKey ? 90 : 15;
  const snapped = Math.round((startRotationDegrees - pointerDeltaDegrees) / increment) * increment;
  return ((snapped % 360) + 360) % 360;
}

export type PlacementBodyDragReleaseOutcome = 'click' | 'cancel' | 'commit';

export interface CampaignInventoryWeightMoveGate {
  accepted: boolean;
  message: string;
}

export interface CampaignInventoryWeightDragAttempt extends CampaignInventoryWeightMoveGate {
  beginGesture: boolean;
  showMovePreview: boolean;
  warningPresentation: 'none' | 'placement-pinned-fade';
}

/**
 * Match inventory_ground_move_weight_gate_required: a GM arranging objects without an acting
 * creature uses scene authority; controlling a creature retains its physical weight gate.
 */
export function campaignInventoryWeightMoveGateRequired(
  campaignView: boolean,
  campaignEditMode: boolean,
  canManageCampaign: boolean,
  hasActiveCaster: boolean,
  storedEncounterInventory: boolean,
): boolean {
  return campaignView
    && !campaignEditMode
    && (!canManageCampaign || hasActiveCaster)
    && storedEncounterInventory;
}

/**
 * Player-authored floor-object movement is physical movement, not scene editing. One unit must fit
 * inside the active creature's push/drag/lift limit before FT3D paints a movable preview. The server
 * repeats this check against authoritative inventory and creature rows.
 */
export function campaignInventoryWeightMoveGate(
  unitWeightLb: number | null | undefined,
  pushDragLiftCapacityLb: number | null | undefined,
  hasActiveCaster: boolean,
): CampaignInventoryWeightMoveGate {
  if (!hasActiveCaster) {
    return { accepted: false, message: 'Control a character before moving this object.' };
  }
  if (!Number.isFinite(unitWeightLb) || Number(unitWeightLb) < 0
    || !Number.isFinite(pushDragLiftCapacityLb) || Number(pushDragLiftCapacityLb) < 0) {
    return {
      accepted: false,
      message: 'That object cannot be moved because its weight limit could not be verified.',
    };
  }
  const weight = Number(unitWeightLb);
  const capacity = Number(pushDragLiftCapacityLb);
  if (weight > capacity + 1e-6) {
    return {
      accepted: false,
      message: `That object weighs ${weight.toLocaleString()} lb; your push, drag, or lift limit is ${capacity.toLocaleString()} lb.`,
    };
  }
  return { accepted: true, message: '' };
}

/** A rejected physical pickup never becomes a drag gesture or movement/drop preview. */
export function campaignInventoryWeightDragAttempt(
  unitWeightLb: number | null | undefined,
  pushDragLiftCapacityLb: number | null | undefined,
  hasActiveCaster: boolean,
): CampaignInventoryWeightDragAttempt {
  const gate = campaignInventoryWeightMoveGate(
    unitWeightLb,
    pushDragLiftCapacityLb,
    hasActiveCaster,
  );
  return {
    ...gate,
    beginGesture: gate.accepted,
    showMovePreview: gate.accepted,
    warningPresentation: gate.accepted ? 'none' : 'placement-pinned-fade',
  };
}

if (!hasCompileAmbient()) {
  (compileAmbient() as Window & {
    __ft3dCampaignPlacementAuthority?: Readonly<{
      inventoryWeightMoveGate: typeof campaignInventoryWeightMoveGate;
      inventoryWeightDragAttempt: typeof campaignInventoryWeightDragAttempt;
      inventoryWeightMoveGateRequired: typeof campaignInventoryWeightMoveGateRequired;
      gmInventoryTransformEditAllowed: typeof campaignGmInventoryTransformEditAllowed;
      gmInventoryRotationEditAllowed: typeof campaignGmInventoryRotationEditAllowed;
      placementRotationSnapDegrees: typeof campaignPlacementRotationSnapDegrees;
    }>;
  }).__ft3dCampaignPlacementAuthority = Object.freeze({
    inventoryWeightMoveGate: campaignInventoryWeightMoveGate,
    inventoryWeightDragAttempt: campaignInventoryWeightDragAttempt,
    inventoryWeightMoveGateRequired: campaignInventoryWeightMoveGateRequired,
    gmInventoryTransformEditAllowed: campaignGmInventoryTransformEditAllowed,
    gmInventoryRotationEditAllowed: campaignGmInventoryRotationEditAllowed,
    placementRotationSnapDegrees: campaignPlacementRotationSnapDegrees,
  });
}

/**
 * Automatic-support pieces may travel above their floor (for example during a jump), but a
 * planar drag/network presentation must never render them below the support they are grounded on.
 * Manual-Z editing intentionally remains unconstrained.
 */
export function campaignSupportedVisualElevationFt(
  requestedElevationFt: number,
  groundingMode: 'automatic-support' | 'manual-z' | string | null | undefined,
  supportPlaneFt: number | null | undefined,
): number {
  if (groundingMode !== 'automatic-support' || !Number.isFinite(supportPlaneFt)) {
    return requestedElevationFt;
  }
  return Math.max(requestedElevationFt, Number(supportPlaneFt));
}

export type CampaignFloorBandBox = FloorBandBox;

/** Resolve one persisted feet/contact coordinate to its containing room floor. */
export { floorBandForPosition, floorBandRoomForPosition };


/**
 * Rebase a stored gameplay row's scene-ground support to its own numeric floor band. The caller
 * derives that band from the row's persisted coordinates with floorBandForPosition; no camera,
 * authored cursor, or nearest-floor fallback participates.
 */
export function storedPlacementFloorSupport<
  T extends { supportSurfaceId: string; supportPlaneFt: number; floorFt?: number | null },
>(support: T | null, placementFloorFt: number | null | undefined): T | null {
  if (!support || !Number.isFinite(placementFloorFt)
    || !/^scene-ground:[^:]+:/.test(support.supportSurfaceId)) return support;
  const floor = Number(placementFloorFt);
  return {
    ...support,
    ...(Object.prototype.hasOwnProperty.call(support, 'floorFt') ? { floorFt: floor } : {}),
    supportSurfaceId: support.supportSurfaceId.replace(
      /^scene-ground:[^:]+:/,
      `scene-ground:${floor}:`,
    ),
    supportPlaneFt: floor,
  };
}

/** A release inside the explicit cancel target never reaches the movement callback. */
export function placementBodyDragReleaseOutcome(
  activated: boolean,
  cancelArmed: boolean,
): PlacementBodyDragReleaseOutcome {
  if (!activated) return 'click';
  return cancelArmed ? 'cancel' : 'commit';
}

/**
 * Horizontal lengths of the landed arcs used to present one jumping move.
 *
 * The current remaining pool is the first boundary. Once it is exhausted, each
 * later arc is capped by the creature's base jumping speed. This is presentation
 * geometry only: AP charging remains with the canonical encounter move logic and
 * no running-jump or clearance rules are inferred here.
 */
export function campaignJumpArcLengthsFt(
  distanceFt: number,
  remainingFt: number,
  baseSpeedFt: number,
): number[] {
  const distance = Number.isFinite(distanceFt) ? Math.max(0, distanceFt) : 0;
  const remaining = Number.isFinite(remainingFt) ? Math.max(0, remainingFt) : 0;
  const baseSpeed = Number.isFinite(baseSpeedFt) ? Math.max(0, baseSpeedFt) : 0;
  if (distance <= 1e-6 || baseSpeed <= 1e-6) return [];

  const lengths: number[] = [];
  let unallocated = distance;
  let currentPool = remaining;
  while (currentPool > 1e-6 && unallocated > 1e-6) {
    const span = Math.min(unallocated, currentPool, baseSpeed);
    lengths.push(span);
    unallocated -= span;
    currentPool -= span;
  }
  while (unallocated > 1e-6) {
    const span = Math.min(unallocated, baseSpeed);
    lengths.push(span);
    unallocated -= span;
  }
  return lengths;
}

/**
 * Tabletop playback pace for standees (and, later, mesh locomotion clips).
 *
 * Ten feet per second keeps an ordinary 30–40 ft move readable instead of
 * looking like a network snap, while the cap prevents very long out-of-combat
 * moves from holding the piece in transit indefinitely.
 */
export function campaignMovePlaybackDurationMs(
  distanceFt: number,
  _speedVariable: string,
): number {
  const distance = Number.isFinite(distanceFt) ? Math.max(0, distanceFt) : 0;
  if (distance <= 1e-6 || _speedVariable === 'teleport') return 0;
  const feetPerSecond = 10;
  return Math.round(Math.min(6000, Math.max(650, distance / feetPerSecond * 1000)));
}

export type CampaignNavigationFootprint = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  widthFt: number;
  depthFt: number;
};

export interface CampaignRenderedMeshFootprintOffsets {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Rebase a measured runtime-mesh footprint onto its persisted FateTide pivot.
 *
 * Registered meshes are not tokens: their catalog size tier is only fallback metadata and must
 * not become the placement boundary once browser geometry is available. Offsets are retained
 * rather than reducing the mesh to width/depth so an intentionally asymmetric asset pivot still
 * validates the visible object the GM is manipulating.
 */
export function campaignRenderedMeshPlacementFootprint<T extends CampaignNavigationFootprint>(
  positionFt: { x: number; y: number },
  renderedOffsetsFt: CampaignRenderedMeshFootprintOffsets | null | undefined,
  fallback: T,
): T {
  const values = renderedOffsetsFt
    ? [renderedOffsetsFt.minX, renderedOffsetsFt.maxX, renderedOffsetsFt.minY, renderedOffsetsFt.maxY]
    : [];
  if (values.length !== 4 || !values.every(Number.isFinite)
    || renderedOffsetsFt!.maxX <= renderedOffsetsFt!.minX
    || renderedOffsetsFt!.maxY <= renderedOffsetsFt!.minY) return fallback;
  return {
    ...fallback,
    minX: positionFt.x + renderedOffsetsFt!.minX,
    maxX: positionFt.x + renderedOffsetsFt!.maxX,
    minY: positionFt.y + renderedOffsetsFt!.minY,
    maxY: positionFt.y + renderedOffsetsFt!.maxY,
    widthFt: renderedOffsetsFt!.maxX - renderedOffsetsFt!.minX,
    depthFt: renderedOffsetsFt!.maxY - renderedOffsetsFt!.minY,
  };
}

export interface CampaignNavigationPlacement<T extends CampaignNavigationFootprint = CampaignNavigationFootprint> {
  forestPart?: string | null;
  footprintReceipt?: T;
  transform?: { positionFt: { x: number; y: number } };
  reviewMetadata?: { role?: string; trunkDiameterFt?: number };
}

/**
 * A stored `tree` row collides as its TRUNK, not its canopy or its size square (owner 2026-09-10: "use
 * meshes for exact in-game dimensions, or mostly their trunks"). Same 0.16 canopy→trunk ratio as the
 * query-only halo policy below, taken over the row's size-tier width × instance scale — the two inputs
 * Janus has for every row (the catalog Tree carries no server-side mesh bounds). Twin of
 * ft_ground_tree_trunk_diameter_ft() in ft_ground_occupancy.php; change both together.
 */
export function ft3dTreeTrunkDiameterFt(sizeWidthFt: number, scale: number): number {
  const width = Number.isFinite(sizeWidthFt) && sizeWidthFt > 0 ? sizeWidthFt : 5;
  const factor = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.min(4, Math.max(1, width * factor * 0.16));
}

export function campaignNavigationCollisionFootprint<T extends CampaignNavigationFootprint>(
  moverKind: 'creature' | 'inventory' | 'prop' | 'status-effect',
  candidate: CampaignNavigationPlacement<T>,
): {
  footprint: T | null;
  policy: 'authored-footprint' | 'tree-trunk-navigation-zone-v1';
} {
  const footprint = candidate.footprintReceipt ?? null;
  const haloTree = candidate.forestPart === 'tree'
    && candidate.reviewMetadata?.role === 'authoritative-halo-item-backed-vegetation';
  const storedTrunkFt = Number(candidate.reviewMetadata?.trunkDiameterFt);
  const storedTree = Number.isFinite(storedTrunkFt) && storedTrunkFt > 0;
  if (!footprint || moverKind === 'prop' || !(haloTree || storedTree) || !candidate.transform) {
    return { footprint, policy: 'authored-footprint' };
  }
  const diameterFt = storedTree
    ? storedTrunkFt
    : Math.min(4, Math.max(1, Math.min(footprint.widthFt, footprint.depthFt) * 0.16));
  const center = candidate.transform.positionFt;
  return {
    footprint: {
      ...footprint,
      minX: center.x - diameterFt / 2,
      maxX: center.x + diameterFt / 2,
      minY: center.y - diameterFt / 2,
      maxY: center.y + diameterFt / 2,
      widthFt: diameterFt,
      depthFt: diameterFt,
    } as T,
    policy: 'tree-trunk-navigation-zone-v1',
  };
}

/**
 * Whether a BLOCKING candidate may be measured from its live rendered mesh instead of its
 * catalog size square.
 *
 * The mover already validates rendered geometry (`campaignRenderedMeshPlacementFootprint`), so
 * leaving every other object on its size tier made one comparison read two different rulers:
 * a `Barracks Chair` kept a 5 ft square around a chair a couple of feet wide and refused a mess
 * table that visibly cleared it by a full cell (owner 2026-09-03).
 *
 * Prop blockers use mesh bounds for creature movers too. Creature blockers retain their size
 * tier, and tree trunks and structural proxies keep their dedicated policies.
 */
export function campaignRenderedMeshCollisionCandidateAllowed(
  _moverKind: 'creature' | 'inventory' | 'prop' | 'status-effect',
  policy: 'authored-footprint' | 'tree-trunk-navigation-zone-v1',
  candidate: { transform?: { positionFt: { x: number; y: number } }; itemType?: string | null; standee?: unknown;
    catalogRevision?: string; candidateId?: string; reviewMetadata?: { role?: string } },
): boolean {
  const creature = candidate.catalogRevision === 'stored-encounter-actors-v1'
    || candidate.candidateId?.startsWith('stored-encounter:')
    || candidate.reviewMetadata?.role === 'support-chain-review-actor';
  return !creature && !candidate.standee && !['door', 'window', 'wall'].includes(candidate.itemType ?? '')
    && policy === 'authored-footprint'
    && Boolean(candidate.transform);
}

export interface CampaignSceneGroundSupport {
  supportSurfaceId: string;
  supportPlaneFt: number;
  canvas: { x: Range; y: Range };
}

export interface CampaignSceneGroundPlacement {
  authority?: string;
  catalogRevision?: string;
  category?: string;
  collisionClass?: string;
  supportScope?: string;
  roomId?: string | null;
  candidateId?: string;
  itemType?: string | null;
  runtime?: unknown;
  meshId?: string | null;
  assetRole?: string;
  standee?: { source?: string };
  procedural?: { kind?: string; source?: string };
  footprintReceipt?: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    insideCanvas?: boolean;
  };
  groundingReceipt?: {
    supportSurfaceKind?: string;
    supportSurfaceId?: string;
    supportAuthorityId?: string;
    supportPlaneFt?: number;
  };
  reviewMetadata?: { sourceEntityId?: string; role?: string };
}

/**
 * Narrow exception for database-backed combatant standees that do not belong to an
 * authored room. They may use scenery-ground only when their source identity, footprint,
 * and grounding receipt all agree with the exact active support surface.
 */
export function isStoredEncounterSceneGroundPlacementAuthorized(
  placement: CampaignSceneGroundPlacement,
  support: CampaignSceneGroundSupport | null,
): boolean {
  return storedEncounterSceneGroundRefusal(placement, support) === null;
}

/**
 * Why a stored row cannot be presented on scenery-ground, or null when it can.
 * Refused rows are omitted individually and named in the GM's scene notice.
 */
export function storedEncounterSceneGroundRefusal(
  placement: CampaignSceneGroundPlacement,
  support: CampaignSceneGroundSupport | null,
): string | null {
  if (!support) return 'no-scene-ground-support';
  if (placement.authority !== 'generated-pinned') return `authority:${placement.authority}`;
  if (placement.collisionClass !== 'inventory') return `collision-class:${placement.collisionClass}`;
  if (placement.supportScope !== 'scene-ground') return `support-scope:${placement.supportScope}`;
  if (placement.roomId !== null) return `room:${placement.roomId}`;
  if (!placement.reviewMetadata?.sourceEntityId) return 'no-source-entity';

  // A stored actor draws either as a stored-encounter STANDEE or, since the FT3D avatar lane
  // (2026-09-09), as its own baked avatar GLB — a `runtime` placement whose assetRole says so.
  // Keying this on the standee alone made the scene REFUSE every avatar-bearing character and
  // stop loading ("Scene-local preview refused a placement outside scenery-ground authority").
  const storedActor = placement.catalogRevision === 'stored-encounter-actors-v1'
    && placement.category === 'review-actors'
    && (placement.standee?.source?.startsWith('stored-encounter-')
      || (Boolean(placement.runtime)
        && (placement.assetRole === 'party-pc-avatar' || placement.assetRole === 'encounter-creature-avatar')));
  // A meshless door/window/wall row draws as its structural proxy, never a standee (owner
  // 2026-09-08/09-10: no image cards on doors, windows or walls). Keying stored inventory on
  // standee-or-mesh alone refused every room wall run the moment its story became active, and
  // the scene stopped loading when a character walked down into a walled basement (2026-09-10).
  const structuralProxyKind = placement.itemType === 'door' ? 'structural-door'
    : placement.itemType === 'window' ? 'structural-window'
      : placement.itemType === 'wall' ? 'structural-wall' : null;
  const storedStructuralProxy = structuralProxyKind !== null
    && !placement.runtime
    && placement.procedural?.source === 'existing-source-row-proxy'
    && placement.procedural.kind === structuralProxyKind;
  const storedInventory = placement.catalogRevision === 'stored-encounter-inventory-v1'
    && placement.category === 'review-dressing'
    && placement.candidateId?.startsWith('stored-inventory:')
    && placement.reviewMetadata.role === 'stored-encounter-inventory'
    && (placement.standee?.source?.startsWith('stored-inventory-')
      || (Boolean(placement.runtime) && Boolean(placement.meshId))
      || storedStructuralProxy);
  if (!storedActor && !storedInventory) return `presentation:${placement.catalogRevision}`;

  const footprint = placement.footprintReceipt;
  const grounding = placement.groundingReceipt;
  if (!footprint
    || ![footprint.minX, footprint.maxX, footprint.minY, footprint.maxY].every(Number.isFinite)) return 'footprint-receipt';
  if (footprint.minX < support.canvas.x.min
    || footprint.maxX > support.canvas.x.max
    || footprint.minY < support.canvas.y.min
    || footprint.maxY > support.canvas.y.max) return 'outside-canvas';
  if (footprint.insideCanvas !== true) return 'footprint-receipt';

  if (grounding?.supportSurfaceKind !== 'scene-ground') return `support-kind:${grounding?.supportSurfaceKind}`;
  if (grounding.supportSurfaceId !== support.supportSurfaceId
    || grounding.supportAuthorityId !== support.supportSurfaceId) {
    return `support-surface:${grounding.supportSurfaceId} != ${support.supportSurfaceId}`;
  }
  // `!(… < ε)`, never `… >= ε`: a NaN support plane must refuse, exactly as the boolean gate did.
  if (!Number.isFinite(grounding.supportPlaneFt)
    || !(Math.abs((grounding.supportPlaneFt ?? Number.NaN) - support.supportPlaneFt) < 1e-6)) {
    return `support-plane:${grounding.supportPlaneFt} != ${support.supportPlaneFt}`;
  }
  return null;
}

/**
 * Floating-point tolerance for the information pick's solid-blocker gate, in the raycast's own
 * units (metres). A tenth of a millimetre — it exists so a surface that IS the blocker survives
 * its own distance, never as a "reach a little way past the wall" allowance.
 *
 * ⚠ Do not grow this to cover a semantic case. A door object bound to an opening is attached
 * under that opening's pivot, so the door row and the blocker are the SAME hit at the same depth
 * and need no slack; a centimetre-scale value would instead let a creature standing just past a
 * thin wall survive the gate (codex-review 2026-09-08).
 */
export const CAMPAIGN_INFORMATION_BLOCKER_TOLERANCE_M = 0.0001;

export interface CampaignInformationPickCandidate {
  placementId: string;
  /** Ray distance to this candidate's OWN surface, in the raycast's units (metres). */
  distance: number;
  creature: boolean;
}

/**
 * Rank the double-click INFORMATION pick. Two rules, in this order:
 *
 * 1. **Solid blockers win over everything.** A candidate farther along the ray than the first
 *    genuinely solid surface (structural wall, door leaf) is dropped outright, so a creature
 *    behind a wall stays unreachable — for a GM exactly as for a player. Perceiving a creature
 *    through fog is a separate axis from clicking it through architecture.
 * 2. **Among what survives, a creature outranks scenery at any depth.** In a forest the canopy
 *    quad in front of a standee used to win a plain depth sort, so double-clicking a creature
 *    opened a tree instead (owner 2026-09-08). Scenery is what a creature hides among, so it
 *    yields; the caller passes only foliage/props here, never architecture.
 *
 * ⚠ OWNER RULING 2026-09-08, accepted deliberately: rule 2 means a creature FULLY hidden inside a
 * bush or behind a boulder is still double-clickable, as long as its own geometry is under the
 * pointer and perception allows it. That is the price of foliage penetration and it is the
 * intended behaviour — do not "fix" it by requiring visible pixels. Perception
 * (`campaignPlacementPerceived`) remains the only thing that hides a creature from the pick.
 *
 * Ties break on placement id, never on pick-registry insertion order, which changes as
 * placements are added and removed during play.
 */
export function campaignInformationPickWinner(
  candidates: readonly CampaignInformationPickCandidate[],
  blockerDistance: number | null = null,
  toleranceM: number = CAMPAIGN_INFORMATION_BLOCKER_TOLERANCE_M,
): CampaignInformationPickCandidate | null {
  const reach = Number.isFinite(blockerDistance)
    ? (blockerDistance as number) + toleranceM
    : null;
  const eligible = candidates
    .filter(candidate => (
      typeof candidate?.placementId === 'string'
      && candidate.placementId !== ''
      && Number.isFinite(candidate.distance)
      && (reach === null || candidate.distance <= reach)
    ))
    .sort((left, right) => {
      const byDistance = left.distance - right.distance;
      if (byDistance !== 0) return byDistance;
      return left.placementId < right.placementId
        ? -1 : left.placementId > right.placementId ? 1 : 0;
    });
  return eligible.find(candidate => candidate.creature) ?? eligible[0] ?? null;
}
